import React, { createContext, useState, useEffect, useContext, useMemo } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { uitloggenEnOpruimen } from '../utils/uitloggen';
import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { leesStartCache, schrijfStartCache, wisStartCache } from '../utils/startCache';
import { vraagAccountSetupAan } from '../utils/accountSetup';

export const AuthContext = createContext();

// Het proefabonnement van een nieuw account wordt server-side gezet door de
// Cloud Function ensureUserAccount (functions/.../auth/accountSetup.js): de
// client mag abonnement/rol/tester-velden niet zelf schrijven (firestore.rules).

// Firebase Auth bevestigt een bewaarde sessie pas na een netwerkronde
// (tokenvernieuwing en accountopvraging), en Firestore wacht daar ook op: bij
// elke koude start bleef een laadscherm staan tot die ronde klaar was. Daarom
// onthoudt de app lokaal wie er het laatst (met een geverifieerd adres)
// ingelogd was. Het dashboard opent dan meteen met de lokaal bewaarde stand
// (utils/startCache.js) en Firebase bevestigt de sessie op de achtergrond;
// klopt de sessie niet meer, dan valt de app op dat moment alsnog terug op
// inloggen. Dit zijn geen geheimen (uid en e-mail): het eigenlijke inlogbewijs
// blijft bij Firebase zelf. Een onthouden sessie geldt alleen voor / en
// /dashboard (zie App.js): al de rest wacht gewoon op Firebase (authReady).
const SESSIE_SLEUTEL = 'flexitime-sessie';

function leesOnthoudenSessie() {
    try {
        const data = JSON.parse(localStorage.getItem(SESSIE_SLEUTEL) || 'null');
        if (data && typeof data === 'object' && typeof data.uid === 'string' && data.uid) {
            return { uid: data.uid, email: typeof data.email === 'string' ? data.email : null };
        }
    } catch {
        // Geblokkeerde of beschadigde opslag: dan gewoon wachten op Firebase zelf.
    }
    return null;
}

function bewaarSessie(gebruiker) {
    try {
        if (gebruiker) localStorage.setItem(SESSIE_SLEUTEL, JSON.stringify(gebruiker));
        else localStorage.removeItem(SESSIE_SLEUTEL);
    } catch {
        // Niet onthouden is geen probleem: de volgende start wacht dan op Firebase.
    }
}

export function AuthProvider({ children }) {
    // Enkel bij het mounten gelezen, daarna is Firebase de bron van waarheid.
    const [onthouden] = useState(leesOnthoudenSessie);
    const [echteUser, setEchteUser] = useState(null);
    // Firebase heeft voor het eerst gezegd of er iemand ingelogd is (of niet).
    const [authReady, setAuthReady] = useState(false);
    const [userDetails, setUserDetails] = useState(null);
    // Het gebruikersdocument is binnen (of mislukt, of er is niemand ingelogd).
    // Dit is de oude betekenis van "loading": pagina's die op userDetails
    // beslissen (rol, abonnement) wachten hierop.
    const [detailsReady, setDetailsReady] = useState(false);
    // Alleen de niet-gevoelige velden die het eerste scherm nodig heeft (naam,
    // onboarding, taal): uit de startcache zolang Firebase nog niet antwoordde.
    // NOOIT rol, abonnement of tester-status hierin: die ontgrendelen dingen in
    // de interface en komen enkel uit Firebase. null = nog niet bekend.
    const [profiel, setProfiel] = useState(() => (onthouden ? leesStartCache(`profiel.${onthouden.uid}`) : null));

    // Zolang Firebase de sessie nog niet bevestigd heeft geldt de onthouden gebruiker,
    // daarna enkel nog wat Firebase zelf zegt. Bevestigd blijft `user` het echte
    // Firebase-object (emailVerified wordt daar in-place bijgewerkt bij reload()).
    const voorlopigeUser = useMemo(
        () => (onthouden ? { uid: onthouden.uid, email: onthouden.email, emailVerified: true } : null),
        [onthouden]
    );
    const user = authReady ? echteUser : (echteUser || voorlopigeUser);
    const loading = !authReady && user === null;

    useEffect(() => {
        // Pas schrijven na de bevestiging van Firebase, anders zou een verlopen sessie
        // meteen weer onthouden worden. Uitloggen, een ongeldige sessie en een nog niet
        // geverifieerd adres wissen het geheugen.
        if (!authReady) return;
        bewaarSessie(echteUser && echteUser.emailVerified ? { uid: echteUser.uid, email: echteUser.email } : null);
        // Niemand (meer) ingelogd of een ander account dan het onthouden account: dan
        // mag er van het vorige account niets lokaal achterblijven (gedeeld toestel).
        if (!echteUser || (onthouden && onthouden.uid !== echteUser.uid)) wisStartCache();
    }, [authReady, echteUser, onthouden]);

    useEffect(() => {
        let unsubscribeUserDoc = null;

        const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
            if (unsubscribeUserDoc) {
                unsubscribeUserDoc();
                unsubscribeUserDoc = null;
            }

            if (currentUser) {
                setEchteUser(currentUser);
                setAuthReady(true);
                setDetailsReady(false);
                setProfiel(leesStartCache(`profiel.${currentUser.uid}`));
                const userDocRef = doc(db, 'users', currentUser.uid);
                let hasTrackedLogin = false;
                let accountSetupKlaar = false;
                let accountSetupBezig = false;
                const requestAccountSetup = () => {
                    if (accountSetupKlaar || accountSetupBezig) return;
                    accountSetupBezig = true;
                    // Het proefabonnement vereist een bevestigd e-mailadres (server-side). Was het adres
                    // nog niet bevestigd, dan blijft accountSetupKlaar false en proberen we het bij de
                    // volgende wijziging van het gebruikersdocument opnieuw (en ook de pagina die het
                    // adres bevestigt vraagt het zelf aan, zie EmailVerificationPage.js). Voorheen werd
                    // het maar één keer per sessie geprobeerd, en dat was meestal te vroeg.
                    vraagAccountSetupAan(currentUser)
                        .then((klaar) => { accountSetupKlaar = klaar; })
                        .finally(() => { accountSetupBezig = false; });
                };

                // Live meeluisteren (onSnapshot) i.p.v. eenmalig ophalen (getDoc):
                // vlak na een nieuwe registratie schrijft Register.js het echte
                // userName pas weg NA het versturen van de verificatiemail (een
                // netwerkcall die even duurt) — deze listener zag op dat moment
                // dus nog geen document, maakte al een placeholder aan
                // ("Gewaardeerde klant") en las daarna nooit opnieuw, waardoor
                // de app de rest van de sessie op die eerste, verouderde
                // snapshot bleef hangen ondanks een correct ingevulde naam.
                // Met een live listener pikt userDetails elke latere wijziging
                // (Register.js, Onboarding.js, instellingen, ...) vanzelf op.
                unsubscribeUserDoc = onSnapshot(userDocRef, (userDoc) => {
                    if (userDoc.exists()) {
                        const userData = userDoc.data();
                        // Document bestaat al (Register.js was sneller) maar het
                        // proefabonnement ontbreekt nog: laat de server dat zetten.
                        if (!userData.subscription && !userData.trialUsed) {
                            requestAccountSetup();
                        }
                        setUserDetails({
                            ...userData,
                            isTester: userData.isTester || false,
                            subscription: userData.subscription || { type: 'free' },
                            userName: userData.userName || 'Gewaardeerde klant', // Voeg gebruikersnaam toe
                            role: userData.role || 'user'
                        });
                        // Enkel de niet-gevoelige velden voor het eerste scherm van de
                        // volgende start (rol/abonnement/tester gaan er bewust niet in).
                        const nieuwProfiel = {
                            userName: userData.userName || null,
                            onboardingCompleted: userData.onboardingCompleted === true,
                            language: userData.language || null
                        };
                        setProfiel(nieuwProfiel);
                        schrijfStartCache(`profiel.${currentUser.uid}`, nieuwProfiel);

                        // Login-moment bijhouden, en een eventuele lopende
                        // inactiviteits-waarschuwing/verwijdertermijn intrekken nu
                        // de gebruiker zich weer heeft aangemeld. Bewust pas NA de
                        // eerste echte snapshot hierboven (niet meteen bij het
                        // opzetten van de listener) — anders past Firestore deze
                        // schrijfactie optimistisch lokaal toe vóór het volledige
                        // document ooit opgehaald is, waardoor de listener (en elke
                        // andere plek die op dat moment hetzelfde document leest,
                        // zoals de onboarding-check in App.js) heel even een
                        // onvolledig document ziet met enkel deze paar nieuwe
                        // velden — andere velden (bv. onboardingCompleted) leken
                        // dan tijdelijk verdwenen, met een ongewenste redirect
                        // terug naar onboarding tot gevolg.
                        if (!hasTrackedLogin) {
                            hasTrackedLogin = true;
                            setDoc(userDocRef, {
                                lastLoginDate: serverTimestamp(),
                                inactivityReminderSentAt: null,
                                scheduledDeletionDate: null
                            }, { merge: true }).catch(error => console.error('Kon lastLoginDate niet bijwerken:', error));
                        }
                    } else if (userDoc.metadata.fromCache) {
                        // Een ontbrekend document uit de lokale cache zegt niets (de
                        // cache kan gewoon nog leeg zijn): wacht op de echte stand van
                        // de server i.p.v. een nieuw account te veronderstellen.
                        return;
                    } else {
                        // Nog geen document (allereerste moment na registratie) —
                        // de server maakt het aan met het proefabonnement; deze
                        // listener vangt het resultaat vanzelf op.
                        setProfiel({ userName: null, onboardingCompleted: false, language: null });
                        requestAccountSetup();
                    }
                    setDetailsReady(true);
                }, (error) => {
                    console.error('Fout bij live volgen van gebruikersdocument:', error);
                    // Zonder bekend profiel (geen cache) valt de app terug op onboarding,
                    // zoals voorheen bij een mislukte opvraging.
                    setProfiel(prev => prev || { userName: null, onboardingCompleted: false, language: null });
                    setDetailsReady(true);
                });
            } else {
                setEchteUser(null);
                setAuthReady(true);
                setUserDetails(null);
                setProfiel(null);
                setDetailsReady(true);
            }
        });

        return () => {
            unsubscribeAuth();
            if (unsubscribeUserDoc) unsubscribeUserDoc();
        };
    }, []);

    // Zie utils/uitloggen.js: ontkoppelt push, wist de lokale gegevens en laadt de pagina opnieuw.
    const logout = () => uitloggenEnOpruimen({ uid: user?.uid, naar: '/' });

    const updateUserName = async (newUserName) => {
        if (user) {
            const userDocRef = doc(db, 'users', user.uid);
            await setDoc(userDocRef, { userName: newUserName }, { merge: true });
            setUserDetails(prevDetails => ({
                ...prevDetails,
                userName: newUserName
            }));
        }
    };

    const value = {
        user,
        userDetails,
        loading,
        authReady,
        detailsReady,
        profiel,
        logout,
        updateUserName // Nieuwe functie om gebruikersnaam bij te werken
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}